import { EN_MESSAGES } from '@jsonfmt/core';
import { createInstance, type i18n as I18nInstance, type InitOptions } from 'i18next';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  EN_COMMON,
  type LocaleDescriptor,
  type LocaleTag,
  locales,
  resolveLocale,
} from '@/locales';
import { STORAGE_KEYS, type StorageService } from '@/services/types';

import { toMessageResource } from './messageCatalog';

/**
 * i18next initialisation, and the one place `<html lang>` is written (ADR-0018).
 *
 * THE RESOURCE SPREAD is the whole architectural point of this file. English lives in two
 * FORMATS and one home each: chrome in `locales/en/common.json`, engine templates in
 * `core/messages/en.ts`. They meet here, once, as
 *
 *     { common: { ...EN_COMMON, message: toMessageResource(EN_MESSAGES) } }
 *
 * — a spread, not a copy and not a sync script. The alternative considered and rejected in
 * ADR-0018 was duplicating `message.*` into the JSON with CI keeping the two in step; that
 * is still two homes for one string, only supervised.
 *
 * WHAT IS DELIBERATELY ABSENT: the `mounted` gate. Every legacy component carried
 * `useState(false)` + `useEffect(() => setMounted(true))` and a
 * `mounted ? t(key) : 'hardcoded English'` ternary at every call site — a Next.js hydration
 * workaround in an app with no SSR. It doubled every string and guaranteed drift. There is
 * no `mounted` here and there must not be one downstream.
 */

export const I18N_NAMESPACE = 'common';

/** Detection order: explicit → stored → browser → `en` (ADR-0018). */
export interface CreateI18nOptions {
  /** Forces a locale. Tests pass it; the app does not. */
  readonly lng?: string | undefined;
  /** The stored preference, read through the storage adapter by the caller. */
  readonly stored?: string | undefined;
  /** Defaults to `navigator.languages`. Passed in so this stays testable in bare Node. */
  readonly navigatorLanguages?: readonly string[] | undefined;
  readonly debug?: boolean | undefined;
}

function negotiate(options: CreateI18nOptions): LocaleDescriptor {
  const candidates: readonly (string | undefined)[] = [
    options.lng,
    options.stored,
    ...(options.navigatorLanguages ?? readNavigatorLanguages()),
  ];
  for (const candidate of candidates) {
    const found = candidate == null ? undefined : resolveLocaleStrict(candidate);
    if (found !== undefined) return found;
  }
  return resolveLocale(DEFAULT_LOCALE);
}

/** `undefined` rather than the default, so negotiation can keep walking the candidate list. */
function resolveLocaleStrict(tag: string): LocaleDescriptor | undefined {
  const resolved = resolveLocale(tag);
  return resolved.tag === DEFAULT_LOCALE && !tag.toLowerCase().startsWith(DEFAULT_LOCALE)
    ? undefined
    : resolved;
}

function readNavigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages ?? (navigator.language ? [navigator.language] : []);
}

/**
 * A fully initialised, self-contained instance.
 *
 * `createInstance()` rather than the `i18next` singleton on purpose: a module-level
 * singleton makes two tests in one file share language state, and it is exactly the global
 * the legacy build then exported as `window.i18n` "for debugging".
 */
export function createI18nInstance(options: CreateI18nOptions = {}): I18nInstance {
  const locale = negotiate(options);
  const instance = createInstance();

  const init: InitOptions = {
    lng: locale.tag,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: locales.map((l) => l.tag),
    load: 'languageOnly',
    ns: [I18N_NAMESPACE],
    defaultNS: I18N_NAMESPACE,
    returnNull: false,
    returnEmptyString: false,
    // The engine's `renderMessage` does its own interpolation over the raw templates, and
    // nothing this app renders is HTML built from a translation, so escaping would only
    // turn a user's `"` into `&quot;` inside a diagnostic.
    interpolation: { escapeValue: false },
    resources: {
      [DEFAULT_LOCALE]: {
        [I18N_NAMESPACE]: { ...EN_COMMON, message: toMessageResource(EN_MESSAGES) },
      },
    },
    // DEV only: a missing key is a loud console line here and a failed `npm run i18n:check`
    // in CI. It is never a raw key on a user's screen, which is what the legacy shipped.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: import.meta.env.DEV
      ? (lngs, ns, key) => {
          console.warn(`i18n: missing key "${key}" in ${ns} for ${lngs.join(', ')}`);
        }
      : false,
    ...(options.debug === undefined ? {} : { debug: options.debug }),
  };

  // `init` is async only because of back-end plugins; with inline resources the instance is
  // usable synchronously on return. The promise is still awaited for its rejection.
  void instance.init(init).catch((error: unknown) => {
    console.error('i18n: initialisation failed', error);
  });

  return instance;
}

/** Loads a locale's chrome catalog and switches to it. Unknown tags are refused, not guessed. */
export async function changeLocale(instance: I18nInstance, tag: LocaleTag): Promise<void> {
  const locale = locales.find((l) => l.tag === tag);
  if (locale === undefined) throw new Error(`unknown locale "${tag}"`);

  if (!instance.hasResourceBundle(locale.tag, I18N_NAMESPACE)) {
    const chrome = await locale.load();
    instance.addResourceBundle(locale.tag, I18N_NAMESPACE, {
      ...chrome,
      // A locale that ships no `message` branch inherits English through `fallbackLng`, and
      // `createMessageCatalog` backstops whatever is still missing.
      message: toMessageResource(EN_MESSAGES),
    });
  }
  await instance.changeLanguage(locale.tag);
}

/** `<html lang>` and `<html dir>` follow the active locale at run time (ADR-0018). */
export function syncDocumentLanguage(instance: I18nInstance): void {
  if (typeof document === 'undefined') return;
  const locale = resolveLocale(instance.resolvedLanguage ?? instance.language);
  document.documentElement.lang = locale.htmlLang;
  document.documentElement.dir = locale.dir;
}

export interface I18nProviderProps {
  readonly children: ReactNode;
  /** Supplied by the composition root. Absent in tests and in the error-boundary fallback. */
  readonly storage?: StorageService | undefined;
  /** An instance built elsewhere (tests, Storybook). One is created when omitted. */
  readonly instance?: I18nInstance | undefined;
}

const parseTag = (raw: unknown): string | null => (typeof raw === 'string' ? raw : null);

export function I18nProvider({ children, storage, instance }: I18nProviderProps): ReactNode {
  // A ref plus lazy `useState` initialiser: the instance is built ONCE, before first paint,
  // and never rebuilt by a re-render. Building it in an effect would make the first frame
  // render raw keys.
  const [i18n] = useState<I18nInstance>(
    () =>
      instance ??
      createI18nInstance({
        stored: storage?.get(STORAGE_KEYS.locale, parseTag, DEFAULT_LOCALE),
      }),
  );

  const storageRef = useRef(storage);
  storageRef.current = storage;

  useEffect(() => {
    syncDocumentLanguage(i18n);
    const onChanged = (): void => {
      syncDocumentLanguage(i18n);
      storageRef.current?.set(STORAGE_KEYS.locale, i18n.resolvedLanguage ?? i18n.language);
    };
    i18n.on('languageChanged', onChanged);
    return () => {
      i18n.off('languageChanged', onChanged);
    };
  }, [i18n]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
